import { OnboardingCompletionContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { Redirect } from "react-router-dom";
import { useTradingIntent } from "features/onboarding/guided/intent";
import Flex from "common/Boxes/Flex";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import OnboardingSteps from "features/onboarding/OnboardingSteps";
import OnboardingConnectExchange from "features/onboarding/connectExchange/OnboardingConnectExchange";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import QuickConnectProvider from "features/configuration/accounts/CreateAccount/QuickConnectProvider";
import {
  EXTERNAL_INVITE_SUCCESS_PATH,
  IS_WHOP,
  ONBOARDING_CONFIRM_SOCIAL_CODE_PATH,
  ONBOARDING_CONNECT_EXCHANGE_PATH,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ONBOARDING_COPY_DCA_BOT_PATH,
  ONBOARDING_DCA_BOT_OVERVIEW_PATH,
  ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH,
  ONBOARDING_SELECT_EXCHANGE_PATH,
  ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
  ONBOARDING_TRADING_METHOD_PATH,
  ONBOARDING_VERIFY_EMAIL_PATH,
} from "common/constants";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import IntentNextStep from "features/onboarding/guided/IntentNextStep";
import CopyDcaBot from "features/onboarding/methodSettings/dcaBot/CopyDcaBot";
import FollowSignalGroup from "features/onboarding/methodSettings/signalsGroup/FollowSignalGroup";
import {
  OnboardingDetailedStep,
  OnboardingStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import BackButton, {
  OnboardingBackSlotContext,
} from "features/onboarding/common/BackButton";
import {
  clearOnboardingLiveChoice,
  hasOnboardingLiveChoice,
} from "features/onboarding/common/utils";
import ConnectExternalAccount from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccount";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import Overview from "features/onboarding/overview/Overview";
import StickyHeader from "common/Headers/StickyHeader";
import VerifyEmail from "features/auth/VerifyEmail";
import ValidateConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ValidateConnectTelegram";
import GroupOverviewProvider from "@shared/features/onboarding/GroupOverviewProvider";
import useOnboardingSteps from "features/onboarding/common/useOnboardingSteps";
import useCreateAccountMessage from "features/configuration/accounts/CreateAccount/useCreateAccountMessage";
import {
  useQueryWithRetries,
  useSelectRealUser,
} from "@shared/utils/hooks/misc";
import useFormattedNotification from "utils/hooks/useFormattedNotification";
import { sharedLib } from "@shared/services/shared.service";
import GroupsPageProvider from "@shared/features/groups/GroupsPageProvider";
import OnboardingSelectExchange from "features/onboarding/connectExchange/OnboardingSelectExchange";
import {
  useBottomNavigator,
  usePollClientsListData,
} from "utils/hooks/misc.tsx";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { setExternalInviteLocalStorageLogin } from "features/configuration/common/utils.ts";
import WhopCreateAccountModal from "features/whop/WhopCreateAccountModal.tsx";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { WorkspaceType } from "@shared/types";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { useSelector } from "react-redux";
import useQuery from "utils/hooks/useQuery";
import ExplorePlatformButton from "features/onboarding/common/ExplorePlatformButton";
import { ExploreOnboardingStepContext } from "features/onboarding/common/ExploreOnboardingStep.context";
import {
  OnboardingStep as AnalyticsOnboardingStep,
  OnboardingStepValueType,
} from "analytics/events";

const OnboardingProvider = () => {
  const [completed, setCompleted] = useState(false);
  const [backSlot, setBackSlot] = useState<HTMLElement | null>(null);
  const [customBackLinks, setCustomBackLinks] = useState(0);
  const registerCustomBackLink = useCallback(() => {
    setCustomBackLinks((n) => n + 1);
    return () => setCustomBackLinks((n) => n - 1);
  }, []);
  const backSlotValue = useMemo(
    () => ({ slot: backSlot, registerCustomLink: registerCustomBackLink }),
    [backSlot, registerCustomBackLink]
  );
  const [declaredExploreStep, setDeclaredExploreStep] =
    useState<OnboardingStepValueType>();
  const { historyPush } = useHistoryWithQuery();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  useQueryWithRetries(sharedLib.sharedApi.common.useGetPrecisionsQuery);
  const { pollClientsListData } = usePollClientsListData();
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const safeArea =
    (contentSafeAreaInset.bottom || 0) + (safeAreaInset.bottom || 0);
  const [whopModalPrimaryExchange, setWhopModalPrimaryExchange] = useState<
    string | undefined
  >();
  const workspace = useSelector(selectWorkspace);
  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const { setWorkspaceType } = useSharedWorkspace({});
  const realUser = useSelectRealUser();
  const query = useQuery();

  const {
    showBackButton,
    changeStep,
    backButtonTitle,
    onBackButtonClick,
    onFinishOnboarding,
    onSkipOnboarding,
    finishOnboardingLoading,
    highLevelStep,
    detailedStep,
  } = useOnboardingSteps();

  // The live choice is carried by `connectLive=1` in the URL. The workspace
  // alone is not enough: a refresh can re-initialise it as the demo one.
  // The param alone is not trusted either (a crafted link could carry it):
  // the in-app choice also leaves a per-tab marker.
  const isLiveChoice =
    query.get("connectLive") === "1" &&
    [
      OnboardingDetailedStep.SELECT_EXCHANGE,
      OnboardingDetailedStep.CONNECT_EXCHANGE,
    ].includes(detailedStep);
  useEffect(() => {
    if (isLiveChoice && isDemoWorkspace && hasOnboardingLiveChoice())
      setWorkspaceType(WorkspaceType.USER);
  }, [isLiveChoice, isDemoWorkspace]);
  useEffect(() => {
    if (!isLiveChoice || completed) clearOnboardingLiveChoice();
  }, [isLiveChoice, completed]);

  const { getSuccessMessage, contextHolder } = useCreateAccountMessage({
    compact: true,
  });
  const { getErrorNotification, contextHolder: errorContextHolder } =
    useFormattedNotification();

  const onFinishValidateTelegram = () =>
    changeStep(OnboardingDetailedStep.CONNECT_EXCHANGE);

  const onSelectExchange = (primaryExchange: string) => {
    if (IS_WHOP && !isDemoWorkspace) {
      setWhopModalPrimaryExchange(primaryExchange);
    } else {
      changeStep(OnboardingDetailedStep.CONNECT_EXCHANGE, {
        exchange: primaryExchange,
      });
    }
  };

  const PATH_TO_COMPONENT: Record<string, React.JSX.Element> = {
    [ONBOARDING_VERIFY_EMAIL_PATH]: <VerifyEmail />,
    [ONBOARDING_CONFIRM_SOCIAL_CODE_PATH]: (
      <ValidateConnectTelegram
        onSuccess={onFinishValidateTelegram}
        onCancel={onFinishValidateTelegram}
      />
    ),
    [ONBOARDING_SELECT_EXCHANGE_PATH]: (
      <OnboardingSelectExchange onSelectExchange={onSelectExchange} />
    ),
    [ONBOARDING_CONNECT_EXCHANGE_PATH]: (
      <OnboardingConnectExchange
        getErrorNotification={getErrorNotification}
        getSuccessMessage={getSuccessMessage}
      />
    ),
    [ONBOARDING_TRADING_METHOD_PATH]: <IntentNextStep />,
    [ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH]: (
      <GroupsPageProvider
        discoveryExchange={
          isDemoWorkspace ? query.get("exchange") || "Binance" : undefined
        }
      >
        <FollowSignalGroup />
      </GroupsPageProvider>
    ),
    [ONBOARDING_COPY_DCA_BOT_PATH]: <CopyDcaBot />,
    [ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH]: <ConnectExternalAccount />,
    [ONBOARDING_DCA_BOT_OVERVIEW_PATH]: (
      <Overview tradingMethod={TradingMethodType.DCA_BOT} />
    ),
    [ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH]: (
      <Overview tradingMethod={TradingMethodType.SIGNALS_GROUP} />
    ),
  };

  const paths = Object.keys(PATH_TO_COMPONENT);
  const components = Object.values(PATH_TO_COMPONENT);
  const [componentIndex] = useRoutesMatch(paths) || [0];
  const isListsPage = [
    OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
    OnboardingDetailedStep.COPY_DCA_BOT,
  ].includes(detailedStep);
  const isConnectExchange = [
    OnboardingDetailedStep.SELECT_EXCHANGE,
    OnboardingDetailedStep.CONNECT_EXCHANGE,
  ].includes(detailedStep);
  const isLiveExchangeConnection =
    detailedStep === OnboardingDetailedStep.CONNECT_EXCHANGE &&
    !isDemoWorkspace;
  const exploreOnboardingStep =
    declaredExploreStep ??
    (highLevelStep === undefined
      ? AnalyticsOnboardingStep.EMAIL_CONFIRMATION
      : STEP_TO_AVO_STEP[highLevelStep]);

  const recoveryEnabled =
    !isExternalConnectAccount &&
    !isDemoWorkspace &&
    detailedStep === OnboardingDetailedStep.CONNECT_EXCHANGE &&
    !query.get("conn_code") &&
    query.get("status_code") !== "200";
  const connectedAccounts =
    sharedLib.sharedApi.configuration.useGetClientsListQuery(undefined, {
      skip: !recoveryEnabled,
      refetchOnMountOrArgChange: true,
    });
  const connectionExchange = query.get("exchange");
  const hasConnectedExchange = connectedAccounts.data?.some(
    (account) =>
      account.is_valid &&
      account.client_status === 1 &&
      connectionExchange &&
      (account.exchange === connectionExchange ||
        account.exchange.startsWith(`${connectionExchange} `))
  );

  useEffect(() => {
    if (
      !recoveryEnabled ||
      connectedAccounts.isFetching ||
      !hasConnectedExchange ||
      !realUser?.has_valid_clients_connected
    )
      return;
    changeStep(OnboardingDetailedStep.TRADING_METHOD, {
      status_code: "",
      fail_message: "",
      exchanges_list: "",
      client_base_name: "",
      guided: "0",
      connectLive: "",
      startMode: "",
    });
  }, [
    recoveryEnabled,
    connectedAccounts.isFetching,
    hasConnectedExchange,
    realUser?.has_valid_clients_connected,
  ]);

  useEffect(() => {
    if (
      pollClientsListData?.accountsCreated &&
      realUser?.has_clients_connected &&
      query.get("guided") !== "1" &&
      isConnectExchange
    ) {
      changeStep(OnboardingDetailedStep.TRADING_METHOD);
    }
  }, [pollClientsListData?.accountsCreated, realUser, isConnectExchange]);

  const { unavailable } = useTradingIntent();
  const contextValue = useMemo(
    () => ({
      changeStep,
      backButtonTitle,
      onBackButtonClick,
      onFinishOnboarding,
      onSkipOnboarding,
      finishOnboardingLoading,
    }),
    [
      backButtonTitle,
      onBackButtonClick,
      finishOnboardingLoading,
      onSkipOnboarding,
    ]
  );

  if (unavailable && detailedStep !== OnboardingDetailedStep.SELECT_EXCHANGE)
    return <Redirect to={ONBOARDING_SELECT_EXCHANGE_PATH} />;

  // Phones: the journey's step count shares the back link's row.
  const backBesideJourney =
    detailedStep !== OnboardingDetailedStep.SELECT_EXCHANGE &&
    !isExternalConnectAccount &&
    highLevelStep !== undefined &&
    query.get("prepareDemo") !== "1" &&
    query.get("changePractice") !== "1";
  return (
    <QuickConnectProvider
      onSubmitSuccess={() => {
        if (isExternalConnectAccount) {
          historyPush(EXTERNAL_INVITE_SUCCESS_PATH);
          setExternalInviteLocalStorageLogin();
        } else {
          changeStep(OnboardingDetailedStep.TRADING_METHOD);
        }
      }}
      isOnboarding={true}
      getSuccessMessage={getSuccessMessage}
      getErrorNotification={getErrorNotification}
      onCreateAccessToken={onSelectExchange}
    >
      <GroupOverviewProvider>
        <OnboardingContext.Provider value={contextValue}>
          <OnboardingCompletionContext.Provider
            value={{ completed, setCompleted }}
          >
            <ExploreOnboardingStepContext.Provider
              value={setDeclaredExploreStep}
            >
              {contextHolder}
              {errorContextHolder}
              <OnboardingBackSlotContext.Provider value={backSlotValue}>
                <Wrapper
                  $isBottomNavigator={isBottomNavigator}
                  $isConnectExchange={isConnectExchange}
                >
                  <BackRow
                    ref={setBackSlot}
                    data-compact={backBesideJourney || undefined}
                  >
                    {showBackButton &&
                      !completed &&
                      !isExternalConnectAccount &&
                      customBackLinks === 0 && (
                        <BackButton detailedStep={detailedStep} />
                      )}
                  </BackRow>
                  {!isExternalConnectAccount &&
                    detailedStep !== OnboardingDetailedStep.SELECT_EXCHANGE && (
                      <StickyHeader style={{ width: "100%" }}>
                        <StepsWrapper>
                          {highLevelStep !== undefined &&
                            query.get("prepareDemo") !== "1" &&
                            query.get("changePractice") !== "1" && (
                              <OnboardingSteps currentStep={highLevelStep} />
                            )}
                        </StepsWrapper>
                      </StickyHeader>
                    )}
                  <ContentWrapper
                    $isListsPage={isListsPage}
                    $isBottomNavigator={isBottomNavigator}
                    $isConnectExchange={isConnectExchange}
                    $safeArea={safeArea}
                  >
                    <StepContent key={paths[componentIndex]}>
                      {components[componentIndex]}
                    </StepContent>
                  </ContentWrapper>
                  {realUser?.is_email_verified &&
                    !isExternalConnectAccount &&
                    !completed &&
                    !isLiveExchangeConnection &&
                    ![
                      OnboardingDetailedStep.SELECT_EXCHANGE,
                      OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
                      // The DCA walkthrough shares this route; it keeps the
                      // explore link like the other methods' walkthroughs.
                      ...(query.get("guided") === "1"
                        ? []
                        : [OnboardingDetailedStep.COPY_DCA_BOT]),
                      OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW,
                      OnboardingDetailedStep.DCA_BOT_OVERVIEW,
                    ].includes(detailedStep) && (
                      <ExploreNavigation
                        $hideOnPhone={
                          detailedStep ===
                            OnboardingDetailedStep.TRADING_METHOD &&
                          query.get("setup") === "1" &&
                          !query.get("preset")
                        }
                      >
                        <ExplorePlatformButton
                          onboardingStep={exploreOnboardingStep}
                        />
                      </ExploreNavigation>
                    )}
                </Wrapper>
              </OnboardingBackSlotContext.Provider>
              {IS_WHOP && (
                <WhopCreateAccountModal
                  open={!!whopModalPrimaryExchange}
                  onClose={() => setWhopModalPrimaryExchange(undefined)}
                  primaryExchange={whopModalPrimaryExchange}
                />
              )}
            </ExploreOnboardingStepContext.Provider>
          </OnboardingCompletionContext.Provider>
        </OnboardingContext.Provider>
      </GroupOverviewProvider>
    </QuickConnectProvider>
  );
};

export default OnboardingProvider;

const STEP_TO_AVO_STEP: Record<OnboardingStep, OnboardingStepValueType> = {
  [OnboardingStep.CONNECT_EXCHANGE]: AnalyticsOnboardingStep.CONNECT_EXCHANGE,
  [OnboardingStep.TRADING_METHOD]: AnalyticsOnboardingStep.TRADING_METHOD,
  [OnboardingStep.METHOD_SETTINGS]: AnalyticsOnboardingStep.METHOD_SETTINGS,
  [OnboardingStep.OVERVIEW]: AnalyticsOnboardingStep.OVERVIEW,
};

const Wrapper = styled(Flex)<{
  $isConnectExchange?: boolean;
  $isBottomNavigator?: boolean;
}>`
  flex-direction: column;
  width: 100%;
  ${(p) =>
    p.$isConnectExchange &&
    p.$isBottomNavigator &&
    `
    height: 100%;
  `};
  flex: 1 0 auto;
  min-height: 100%;
  justify-content: flex-start;
  background: ${(p) => p.theme.colors.canvasBgColor};
  /* Room for a fixed bottom bar, within the screen's height. */
  box-sizing: border-box;
  padding-bottom: var(--onboarding-action-bar, 0px);
  /* Phones: taps answer like a native app's, with a press instead of the
     browser's grey flash. */
  @media (max-width: 560px) {
    /* Steps fade in without sliding up (onboardingUi's reveal). */
    --onboarding-reveal-rise: 0px;
    -webkit-tap-highlight-color: transparent;
    .ant-btn {
      transition: transform 0.12s ease;
    }
    .ant-btn:not(:disabled):active {
      transform: scale(0.97);
    }
    /* The bottom bars' buttons in a native app's proportions. */
    [data-onboarding-bar] .ant-btn-primary {
      height: 50px;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
    }
  }
`;
const ContentWrapper = styled.div<{
  $isListsPage?: boolean;
  $isConnectExchange?: boolean;
  $isBottomNavigator?: boolean;
  $safeArea: number;
}>`
  box-sizing: border-box;
  height: fit-content;
  /* Telegram's bottom inset, unless a fixed bottom bar (which keeps clear
     of it itself) takes that room. */
  margin: ${(p) =>
    `0 auto max(0px, calc(${p.$safeArea}px - var(--onboarding-action-bar, 0px))) auto`};
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  padding: 0 16px 20px 16px;
  /* Phones: the 20px gutter, except where the step's content brings its
     own (the lists, the exchange screens). Media rules come after the ones
     below, so the exceptions are repeated here. */
  @media (max-width: 560px) {
    /* Steps that run edge to edge read it (--onboarding-step-padding). */
    --onboarding-step-padding: ${(p) =>
      p.$isListsPage || (p.$isConnectExchange && p.$isBottomNavigator)
        ? 0
        : ONBOARDING_PHONE_GUTTER}px;
    padding: 0 var(--onboarding-step-padding) 20px;
    /* The step takes the rest of the screen, so the explore link under it
       starts at the bottom edge and doesn't jump down as the step loads. */
    flex: 1 0 auto;
  }
  /* The list steps keep the 16px gutter above phone size too: their own
     layout pads only on phones, so tablets ran edge to edge. */
  /* Clip sideways without becoming a scroll container (overflow: hidden or
     auto would), so sticky parts of a step (the method choice's Continue
     panel) stick to the page's scroller. */
  overflow-x: clip;
  ${(p) =>
    p.$isConnectExchange &&
    p.$isBottomNavigator &&
    `
    padding: 0 0 20px 0;
    height: 100%;
    overflow-y: auto;
  `};
`;
// The one place every step's back link lives (see BackButton.tsx).
// Same 1040px column and 16px gutter as the step content, so the link sits at
// the column's top-left; collapses completely when a step has no back link.
// Phones: with the journey beside it (data-compact), the step count sits at
// the right end of the back link's row.
const BackRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1072px;
  margin: 0 auto;
  padding: 12px 16px 0;
  align-self: center;
  &:empty {
    display: none;
  }
  /* Phones: right under the header; the link's 40px tap area is the space.
     Nothing sticks there: the journey line (next) scrolls with the step. */
  @media (max-width: 560px) {
    padding: 0 ${ONBOARDING_PHONE_GUTTER}px;
    & + * {
      position: static !important;
      box-shadow: none !important;
    }
  }
`;
const StepsWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  min-height: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: center;
  background: ${(p) => p.theme.colors.canvasBgColor};
  @media (max-width: 650px) {
    padding: 8px 20px;
  }
  /* Phones: the journey is one line, at the step's 16px gutter. */
  @media (max-width: 560px) {
    justify-content: flex-start;
    /* Right under the back link's 40px tap area (about 12px of space). */
    padding: 0 ${ONBOARDING_PHONE_GUTTER}px;
    /* With a back link: one compact row, the back link on the left and the
       step count on the right, as a native screen's top bar. */
    ${BackRow}[data-compact="true"]:not(:empty) + * & {
      /* Up by the link's 40px tap area and its 8px bottom margin. */
      margin-top: -48px;
      min-height: 40px;
      align-items: center;
      justify-content: flex-end;
      background: transparent;
      pointer-events: none;
      p {
        width: auto;
      }
      [data-journey-section] {
        display: none;
      }
    }
  }
`;
// Phones hide it under the setup form, which fills the screen.
const ExploreNavigation = styled.nav<{ $hideOnPhone?: boolean }>`
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 24px 20px 20px;
  @media (max-width: 650px) {
    padding-top: 0;
  }
  button {
    min-height: 44px;
  }
  @media (max-width: 560px) {
    ${(p) => p.$hideOnPhone && "display: none;"}
  }
`;
const StepContent = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
`;
